"use client"

import '../../app/mockups/_styles/editorial.css'
import * as React from "react"

export interface EditorialTocSection {
  id: string
  title: string
}

interface EditorialTocProps {
  sections: EditorialTocSection[]
  /** Top offset in px to use both for IntersectionObserver rootMargin AND for the section
   *  scroll-margin-top on click. Default 112 — clears the 92px sticky site header
   *  (86px content + 6px orange border) with ~20px of breathing room. */
  topOffset?: number
  /** Eyebrow label above the list. Default "On this page". */
  label?: string
  /** Apply `ed-mobile-hidden` so the TOC only renders on lg+ screens. Default true. */
  hideOnMobile?: boolean
}

/**
 * Editorial table-of-contents with smooth scroll + scrollspy active highlight.
 *
 * Lifted as a brand-kit primitive so legal pages, blog posts, and any future
 * long-form template can reuse one TOC implementation. Behaviors:
 *
 *  - Click a link → preventDefault + scroll to `#id` with scroll-margin-top
 *    so the heading lands below the sticky header (not under it).
 *  - IntersectionObserver watches every `<section id="…">` in the page and
 *    highlights the matching TOC item via the `.active` class as the section
 *    enters the upper third of the viewport.
 *  - URL hash updates on click (without triggering a hard scroll, since
 *    `scrollIntoView` is doing the work).
 *
 * Styling lives in `app/mockups/_styles/editorial.css` under `.ed-toc` —
 * `.ed-toc li.active` is already wired to switch color to fridge-orange.
 */
export function EditorialToc({
  sections,
  topOffset = 112,
  label = "On this page",
  hideOnMobile = true,
}: EditorialTocProps) {
  const [activeId, setActiveId] = React.useState<string>(sections[0]?.id || "")

  React.useEffect(() => {
    if (typeof window === "undefined" || sections.length === 0) return

    // Build a map of id → DOM node so we don't query the DOM on every observer fire.
    const sectionEls = sections
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => el !== null)
    if (sectionEls.length === 0) return

    // The observer marks a section "intersecting" once any part of it sits in the
    // top 30% of the viewport. This is the strip readers actually focus on; using
    // the full viewport leads to long sections marking two TOC items as active at
    // once. The header takes ~88px so we offset by `topOffset` from the top edge.
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to the top of the trigger band that's intersecting.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]?.target.id) {
          setActiveId(visible[0].target.id)
        }
      },
      {
        rootMargin: `-${topOffset}px 0px -65% 0px`,
        threshold: 0,
      },
    )

    sectionEls.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [sections, topOffset])

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - topOffset
    window.scrollTo({ top: y, behavior: "smooth" })
    // Update URL hash without triggering native scroll (which would ignore our offset).
    if (typeof history !== "undefined") {
      history.replaceState(null, "", `#${id}`)
    }
    setActiveId(id)
  }

  // Outer wrapper stretches to the grid row's height (`align-self: stretch`)
  // so the inner sticky nav has scroll-room to "follow" the article column.
  // Without this, parent grids that use `align-items: start` shrink the cell
  // to the TOC's content height — leaving sticky with nothing to scroll past
  // and the TOC ends up pinned to the top edge of its own (tiny) cell instead
  // of the viewport.
  return (
    <div className={`ed-toc-wrap${hideOnMobile ? " ed-mobile-hidden" : ""}`}>
      {/* FRIDGE-149 §2 — the label is NOT a heading: the TOC sits before the
          article's H1 in the DOM (left column), so an <h4> here put a level-4
          heading ahead of the H1 and skipped H2/H3. The nav is named by the
          same label instead; the inline styles keep the look identical. */}
      <nav className="ed-toc" style={{ top: topOffset }} aria-label={label}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.15em",
            color: "var(--fridge-blue)",
            textTransform: "uppercase",
            margin: "0 0 12px",
          }}
        >
          {label}
        </div>
        <ol>
          {sections.map((s) => (
            <li key={s.id} className={s.id === activeId ? "active" : ""}>
              <a href={`#${s.id}`} onClick={(e) => handleClick(e, s.id)}>
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </div>
  )
}
